import { useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import PageMeta from '../../components/SEO/PageMeta';
import ConversionTool from '../../components/ConversionTool/ConversionTool';
import {
  buildConversionSlug,
  FORMAT_LABELS,
  getConversionDescription,
  getConversionTitle,
  isConversionSupported,
  parseConversionSlug,
} from '../../config/conversions';
import {
  getImageConverterStructuredData,
  getImageSeoContent,
  SeoContentItem,
} from '../../config/imageSeoContent';
import { IMAGE_FORMATS, ImageFormat } from '../../types/media';
import { useAppDispatch } from '../../store/hooks';
import { resetConversion } from '../../store/conversionSlice';
import styles from './ConversionPage.module.css';

export default function ConversionPage() {
  const { conversionSlug } = useParams<{ conversionSlug: string }>();
  const dispatch = useAppDispatch();
  const parsed = conversionSlug ? parseConversionSlug(conversionSlug) : null;

  useEffect(() => {
    return () => {
      dispatch(resetConversion());
    };
  }, [dispatch, conversionSlug]);

  if (!parsed) {
    return <Navigate to="/" replace />;
  }

  const { from, to } = parsed;
  const path = `/media/${conversionSlug}`;
  const seoContent = getImageSeoContent(from, to);
  const structuredData = getImageConverterStructuredData(from, to, path);

  return (
    <>
      <PageMeta
        title={getConversionTitle(from, to)}
        description={getConversionDescription(from, to)}
        path={path}
        structuredData={structuredData}
      />

      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span aria-hidden> / </span>
        <span>
          {FORMAT_LABELS[from]} to {FORMAT_LABELS[to]}
        </span>
      </nav>

      <ConversionTool from={from} to={to} />

      <section className={styles.seoContent} aria-labelledby="how-it-works-heading">
        <header className={styles.seoIntro}>
          <p className={styles.eyebrow}>{seoContent.conversionName} guide</p>
          <h2 id="how-it-works-heading">How {seoContent.conversionName} conversion works</h2>
          <p>
            Use this focused guide to understand when to convert {FORMAT_LABELS[from]} images to{' '}
            {FORMAT_LABELS[to]}, what to expect from the output, and how to fix common conversion
            issues.
          </p>
        </header>

        <ContentGrid items={seoContent.howItWorks} ordered />
      </section>

      <SeoSection
        title={`${seoContent.conversionName} examples`}
        description={`Realistic ways to use this ${FORMAT_LABELS[from]} to ${FORMAT_LABELS[to]} converter.`}
        items={seoContent.examples}
      />

      <SeoSection
        title={`Benefits and use cases for ${seoContent.conversionName}`}
        description={`Why ${FORMAT_LABELS[to]} can be the right output format for this image conversion.`}
        items={seoContent.benefits}
      />

      <SeoSection
        title={`${seoContent.conversionName} troubleshooting`}
        description="Quick fixes for upload, conversion, quality, and download issues."
        items={seoContent.troubleshooting}
      />

      <section className={styles.faqSection} aria-labelledby="faq-heading">
        <div>
          <p className={styles.eyebrow}>FAQs</p>
          <h2 id="faq-heading">{seoContent.conversionName} FAQs</h2>
        </div>
        <div className={styles.faqList}>
          {seoContent.faqs.map((faq) => (
            <details key={faq.title} className={styles.faqItem}>
              <summary>{faq.title}</summary>
              <p>{faq.body}</p>
            </details>
          ))}
        </div>
      </section>

      <aside className={styles.related} aria-labelledby="related-heading">
        <h2 id="related-heading">Other conversions from {FORMAT_LABELS[from]}</h2>
        <RelatedLinks from={from} currentTo={to} />
      </aside>
    </>
  );
}

function SeoSection({
  title,
  description,
  items,
}: {
  title: string;
  description: string;
  items: SeoContentItem[];
}) {
  return (
    <section
      className={styles.seoContent}
      aria-labelledby={title.toLowerCase().replace(/\W+/g, '-')}
    >
      <header className={styles.seoIntro}>
        <h2 id={title.toLowerCase().replace(/\W+/g, '-')}>{title}</h2>
        <p>{description}</p>
      </header>
      <ContentGrid items={items} />
    </section>
  );
}

function ContentGrid({ items, ordered = false }: { items: SeoContentItem[]; ordered?: boolean }) {
  const ListTag = ordered ? 'ol' : 'ul';

  return (
    <ListTag className={ordered ? styles.stepGrid : styles.cardGrid}>
      {items.map((item) => (
        <li key={item.title} className={styles.contentCard}>
          <h3>{item.title}</h3>
          <p>{item.body}</p>
        </li>
      ))}
    </ListTag>
  );
}

function RelatedLinks({ from, currentTo }: { from: ImageFormat; currentTo: ImageFormat }) {
  return (
    <ul className={styles.relatedList}>
      {IMAGE_FORMATS.filter(
        (f) => f !== from && f !== currentTo && isConversionSupported(from, f),
      ).map((to) => (
        <li key={to}>
          <Link to={`/media/${buildConversionSlug(from, to)}`}>
            {FORMAT_LABELS[from]} → {FORMAT_LABELS[to]}
          </Link>
        </li>
      ))}
    </ul>
  );
}
